"use client"

import { useMemo, useState } from "react"
import type { BulkEmail } from "@/schema"
import { Button, Popover, PopoverContent, PopoverTrigger, ScrollArea } from "@ui/shad"
import { Megaphone } from "lucide-react"

import { useFetchBulkEmails } from "@/hooks/api/bulk_emails"

import { BulkEmailDetailDialog } from "./detail-dialog"
import { buildBulkEmailStatusDescription, BulkEmailStatusItem, type BulkEmailStatusDescription } from "./status-item"

type BulkEmailRun = BulkEmail & { description: BulkEmailStatusDescription }

export function BulkEmailStatusPopover() {
  const { data: bulkEmailsResponse } = useFetchBulkEmails()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedBulkEmailId, setSelectedBulkEmailId] = useState<string | null>(null)

  const bulkEmails = useMemo(() => bulkEmailsResponse?.bulk_emails ?? [], [bulkEmailsResponse])
  const bulkEmailRuns: BulkEmailRun[] = useMemo(
    () =>
      bulkEmails.map((bulkEmail) => ({
        ...bulkEmail,
        description: buildBulkEmailStatusDescription(bulkEmail),
      })),
    [bulkEmails]
  )

  const activeCount = useMemo(
    () => bulkEmails.filter((email) => email.status === "scheduled" || email.status === "sending").length,
    [bulkEmails]
  )

  const handleSelectBulkEmail = (id: string) => {
    setSelectedBulkEmailId(id)
    setIsPopoverOpen(false)
  }

  const detailsDialog = selectedBulkEmailId ? (
    <BulkEmailDetailDialog bulkEmailId={selectedBulkEmailId} onClose={() => setSelectedBulkEmailId(null)} />
  ) : null

  if (bulkEmails.length === 0) {
    return detailsDialog
  }

  return (
    <>
      {detailsDialog}
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="iconSm" className="relative">
            <Megaphone className="size-4" />
            {activeCount > 0 && (
              <span className="bg-tertiary-foreground text-background absolute top-0 right-0 flex size-4 items-center justify-center rounded-full text-[10px]">
                {activeCount}
              </span>
            )}
            <span className="sr-only">Bulk email history</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" alignOffset={-20} className="w-96 p-0">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold">Bulk email history</h3>
          </div>
          <ScrollArea className="max-h-80">
            <div className="divide-y">
              {bulkEmailRuns.map((bulkEmail) => (
                <BulkEmailStatusItem
                  key={bulkEmail.id}
                  bulkEmail={bulkEmail}
                  description={bulkEmail.description}
                  onClick={() => handleSelectBulkEmail(bulkEmail.id)}
                />
              ))}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </>
  )
}
